import { Metric, MetricData } from '@laborability/commons';
import { Line } from 'react-chartjs-2';
import { DateAggregationEnum } from './types';
import dayjs from 'dayjs';

interface Props {
  metricData: Metric;
  metricDataLabel: string;
  borderColor: string;
  backgroundColor: string;
  comparisionMetricData?: Metric;
  comparisionMetricDataLabel?: string;
  comparisionBorderColor: string;
  comparisionBackgroundColor: string;
  dateAggregation: DateAggregationEnum;
}

export default function Chart({
  metricData,
  borderColor,
  backgroundColor,
  dateAggregation,
  comparisionBackgroundColor,
  comparisionBorderColor,
  metricDataLabel,
  comparisionMetricData,
  comparisionMetricDataLabel,
}: Props) {
  const getStartOfRange = (date: string) => {
    return dayjs(date, 'YYYY-MM-DD')
      .startOf(dateAggregation)
      .locale('it')
      .format(
        dateAggregation === DateAggregationEnum.month
          ? 'MMMM YYYY'
          : 'DD-MM-YYYY',
      );
  };

  const groupValues = (data: MetricData[]) => {
    const grouped: { [key: string]: number } = {};
    data.forEach(current => {
      const currentDate = getStartOfRange(current.date);
      grouped[currentDate] = (grouped[currentDate] || 0) + current.value;
    });
    return Object.values(grouped);
  };

  const getLabels = (data: MetricData[]) => {
    const grouped: { [key: string]: number } = {};
    data.forEach(current => {
      const currentDate = getStartOfRange(current.date);
      grouped[currentDate] = (grouped[currentDate] || 0) + current.value;
    });
    const count = Object.keys(grouped).length;
    switch (dateAggregation) {
      case DateAggregationEnum.day:
        return Array.from({ length: count }, (_, i) => `Giorno ${i + 1}`);
      case DateAggregationEnum.week:
        return Array.from({ length: count }, (_, i) => `Settimana ${i + 1}`);
      case DateAggregationEnum.month:
        return Array.from({ length: count }, (_, i) => `Mese ${i + 1}`);
      default:
        return Array.from({ length: count }, (_, i) => `Periodo ${i + 1}`);
    }
  };

  const labels =
    metricData.dimensions.length > 0
      ? getLabels(metricData.dimensions[0].data)
      : [];

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
      }}
    >
      <Line
        data={{
          labels,
          datasets: [
            ...metricData.dimensions.map(dimension => ({
              label: metricDataLabel,
              borderColor,
              backgroundColor,
              data: groupValues(dimension.data),
            })),
            ...(comparisionMetricData
              ? comparisionMetricData.dimensions.map(dimension => ({
                  label: comparisionMetricDataLabel ?? '',
                  borderColor: comparisionBorderColor,
                  backgroundColor: comparisionBackgroundColor,
                  data: groupValues(dimension.data),
                }))
              : []),
          ],
        }}
        options={{
          plugins: {
            legend: {
              display: true,
              onClick: () => {},
            },
          },
        }}
      />
    </div>
  );
}
